'use client';

import { useState, useCallback } from 'react';
import type { MediaItem } from './types';
import styles from './mediaGallery.module.scss';

// ─────────────────────────────────────────────────────────────────────────────
// Lightbox Dialog (for viewing single media item)
// ─────────────────────────────────────────────────────────────────────────────

type LightboxProps = {
  media: MediaItem;
  onClose: () => void;
};

function Lightbox({ media, onClose }: LightboxProps) {
  return (
    <div
      className={styles.lightboxOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="View media"
    >
      <button
        type="button"
        className={styles.lightboxClose}
        onClick={onClose}
        aria-label="Close"
      >
        ×
      </button>
      <div
        className={styles.lightboxContent}
        onClick={(e) => e.stopPropagation()}
      >
        {media.type === 'video' ? (
          <video
            src={media.src}
            controls
            autoPlay
            className={styles.lightboxMedia}
          />
        ) : (
          <img
            src={media.src}
            alt={media.alt || 'Media'}
            className={styles.lightboxMedia}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Gallery Modal (for viewing all media)
// ─────────────────────────────────────────────────────────────────────────────

type GalleryModalProps = {
  media: MediaItem[];
  initialIndex?: number;
  onClose: () => void;
};

function GalleryModal({ media, initialIndex = 0, onClose }: GalleryModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const current = media[currentIndex];

  const goTo = useCallback(
    (index: number) => {
      if (index >= 0 && index < media.length) {
        setCurrentIndex(index);
      }
    },
    [media.length]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goTo(currentIndex - 1);
      if (e.key === 'ArrowRight') goTo(currentIndex + 1);
      if (e.key === 'Escape') onClose();
    },
    [currentIndex, goTo, onClose]
  );

  return (
    <div
      className={styles.galleryModalOverlay}
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="dialog"
      aria-modal="true"
      aria-label="Gallery"
    >
      <button
        type="button"
        className={styles.galleryModalClose}
        onClick={onClose}
        aria-label="Close gallery"
      >
        ×
      </button>

      <div
        className={styles.galleryModalContent}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main display */}
        <div className={styles.galleryModalMain}>
          {media.length > 1 && (
            <button
              type="button"
              className={`${styles.galleryModalNav} ${styles.galleryModalNavPrev}`}
              onClick={() => goTo(currentIndex - 1)}
              disabled={currentIndex === 0}
              aria-label="Previous"
            >
              ‹
            </button>
          )}

          <div className={styles.galleryModalDisplay}>
            {current?.type === 'video' ? (
              <video
                key={current.src}
                src={current.src}
                controls
                autoPlay
                className={styles.galleryModalMedia}
              />
            ) : (
              <img
                key={current?.src}
                src={current?.src}
                alt={current?.alt || `Photo ${currentIndex + 1}`}
                className={styles.galleryModalMedia}
              />
            )}
          </div>

          {media.length > 1 && (
            <button
              type="button"
              className={`${styles.galleryModalNav} ${styles.galleryModalNavNext}`}
              onClick={() => goTo(currentIndex + 1)}
              disabled={currentIndex === media.length - 1}
              aria-label="Next"
            >
              ›
            </button>
          )}
        </div>

        {/* Thumbnail strip */}
        {media.length > 1 && (
          <div className={styles.galleryModalThumbnails}>
            <span className={styles.galleryModalCount}>
              {currentIndex + 1} / {media.length}
            </span>
            <div className={styles.galleryModalThumbnailStrip}>
              {media.map((item, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`${styles.galleryModalThumb} ${idx === currentIndex ? styles.galleryModalThumbActive : ''}`}
                  onClick={() => goTo(idx)}
                  aria-label={`View photo ${idx + 1}`}
                >
                  <img
                    src={item.thumbnailSrc || item.src}
                    alt=""
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Media Item Component
// ─────────────────────────────────────────────────────────────────────────────

type MediaItemDisplayProps = {
  media: MediaItem;
  className?: string;
  onClick?: () => void;
  priority?: boolean;
};

function MediaItemDisplay({
  media,
  className,
  onClick,
  priority = false
}: MediaItemDisplayProps) {
  const isVideo = media.type === 'video';
  const displaySrc =
    isVideo && media.thumbnailSrc ? media.thumbnailSrc : media.src;

  return (
    <button
      type="button"
      className={`${styles.mediaItem} ${className || ''}`}
      onClick={onClick}
      aria-label={media.alt || 'View media'}
    >
      {isVideo ? (
        <>
          <img
            src={displaySrc}
            alt={media.alt || 'Video thumbnail'}
            className={styles.mediaImage}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
          />
          <div className={styles.videoOverlay}>
            <span className={styles.playIcon}>▶</span>
          </div>
        </>
      ) : (
        <img
          src={media.src}
          alt={media.alt || 'Photo'}
          className={styles.mediaImage}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : undefined}
        />
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MediaGallery Component
// ─────────────────────────────────────────────────────────────────────────────

export type MediaGalleryProps = {
  /** Array of media items */
  media: MediaItem[];
  /** Maximum visible items before "show all" (default 4) */
  maxVisible?: number;
  /** Use 3-item layout when >= 3 items (default true) */
  threeItemLayout?: boolean;
  /** Hide "show all" button */
  hideShowAll?: boolean;
  /** Show tiled paw print background behind main image */
  tiledBackground?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Label for accessibility */
  ariaLabel?: string;
};

export function MediaGallery({
  media,
  maxVisible = 4,
  threeItemLayout = true,
  hideShowAll = false,
  tiledBackground = false,
  className,
  ariaLabel = 'Photo gallery'
}: MediaGalleryProps) {
  const [lightboxMedia, setLightboxMedia] = useState<MediaItem | null>(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  const validMedia = media.filter((m) => m && m.src);

  const handleMediaClick = useCallback((item: MediaItem, index: number) => {
    setGalleryStartIndex(index);
    setShowGalleryModal(true);
  }, []);

  const handleShowAll = useCallback(() => {
    setGalleryStartIndex(0);
    setShowGalleryModal(true);
  }, []);

  if (validMedia.length === 0) {
    return (
      <div
        className={`${styles.gallery} ${styles.galleryEmpty} ${className || ''}`}
      >
        <div className={styles.emptyPlaceholder}>
          <span className={styles.emptyIcon} aria-hidden="true">
            📷
          </span>
          <span className={styles.emptyText}>No photos available</span>
        </div>
      </div>
    );
  }

  // Three-item layout: 1 main + 2 sidebar
  if (threeItemLayout && validMedia.length >= 3) {
    const [main, second, third] = validMedia;
    return (
      <>
        <div
          className={`${styles.gallery} ${styles.galleryThreeItem} ${className || ''}`}
          role="region"
          aria-label={ariaLabel}
        >
          <div
            className={`${styles.galleryMain} ${tiledBackground ? styles.galleryMainTiled : ''}`}
          >
            <MediaItemDisplay
              media={main!}
              className={`${styles.mainImage} ${tiledBackground ? styles.mainImageContain : ''}`}
              onClick={() => handleMediaClick(main!, 0)}
              priority
            />
          </div>
          <div className={styles.gallerySidebar}>
            <MediaItemDisplay
              media={second!}
              className={styles.sidebarImage}
              onClick={() => handleMediaClick(second!, 1)}
            />
            <MediaItemDisplay
              media={third!}
              className={styles.sidebarImage}
              onClick={() => handleMediaClick(third!, 2)}
            />
          </div>
          {!hideShowAll && (
            <button
              type="button"
              className={styles.showAllButton}
              onClick={handleShowAll}
            >
              Show all
            </button>
          )}
        </div>

        {showGalleryModal && (
          <GalleryModal
            media={validMedia}
            initialIndex={galleryStartIndex}
            onClose={() => setShowGalleryModal(false)}
          />
        )}
      </>
    );
  }

  // Two-item layout: side by side
  if (validMedia.length === 2) {
    const [first, second] = validMedia;
    return (
      <>
        <div
          className={`${styles.gallery} ${styles.galleryTwoItem} ${className || ''}`}
          role="region"
          aria-label={ariaLabel}
        >
          <MediaItemDisplay
            media={first!}
            className={styles.halfImage}
            onClick={() => handleMediaClick(first!, 0)}
            priority
          />
          <MediaItemDisplay
            media={second!}
            className={styles.halfImage}
            onClick={() => handleMediaClick(second!, 1)}
          />
        </div>

        {showGalleryModal && (
          <GalleryModal
            media={validMedia}
            initialIndex={galleryStartIndex}
            onClose={() => setShowGalleryModal(false)}
          />
        )}
      </>
    );
  }

  // Single-item layout
  if (validMedia.length === 1) {
    const [single] = validMedia;
    return (
      <>
        <div
          className={`${styles.gallery} ${styles.gallerySingleItem} ${className || ''}`}
          role="region"
          aria-label={ariaLabel}
        >
          <MediaItemDisplay
            media={single!}
            className={styles.singleImage}
            onClick={() => handleMediaClick(single!, 0)}
            priority
          />
        </div>

        {lightboxMedia && (
          <Lightbox
            media={lightboxMedia}
            onClose={() => setLightboxMedia(null)}
          />
        )}
        {showGalleryModal && (
          <GalleryModal
            media={validMedia}
            initialIndex={galleryStartIndex}
            onClose={() => setShowGalleryModal(false)}
          />
        )}
      </>
    );
  }

  // Grid layout for non-three-item layout
  const displayMedia = validMedia.slice(0, maxVisible);
  const remainingCount = validMedia.length - maxVisible;

  return (
    <>
      <div
        className={`${styles.gallery} ${styles.galleryGrid} ${className || ''}`}
        role="region"
        aria-label={ariaLabel}
      >
        {displayMedia.map((item, idx) => (
          <MediaItemDisplay
            key={idx}
            media={item}
            className={styles.gridImage}
            onClick={() => handleMediaClick(item, idx)}
            priority={idx === 0}
          />
        ))}
        {!hideShowAll && remainingCount > 0 && (
          <button
            type="button"
            className={styles.showAllButton}
            onClick={handleShowAll}
          >
            +{remainingCount} more
          </button>
        )}
      </div>

      {showGalleryModal && (
        <GalleryModal
          media={validMedia}
          initialIndex={galleryStartIndex}
          onClose={() => setShowGalleryModal(false)}
        />
      )}
    </>
  );
}
